import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../contexts/LocalizationContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Separator } from '../components/ui/separator';
import { useToast } from '../hooks/use-toast';
import { formatPrice } from '../utils/currency';
import { Package, Truck, Clock, CreditCard, Lock, Store } from 'lucide-react';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { allowsManualNumber, addressExample, isValidHouseNumber } from '../utils/addressRules';
import LockedCountrySelect from '../components/LockedCountrySelect';
import { DIAL_CODE_BY_COUNTRY, PHONE_RULES, flagEmoji } from '../utils/dialCodes';
import SEOHead from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Address fields filled only by the Google Maps search box.
const LOCKED_FIELD = 'bg-gray-100 text-gray-700 cursor-not-allowed';

// Helper function to get localized name
const getLocalizedName = (name, lang = 'es') => {
  if (!name) return '';
  if (typeof name === 'string') return name;
  if (typeof name === 'object') {
    return name[lang] || name.es || name.en || Object.values(name)[0] || '';
  }
  return String(name);
};

const NewCheckoutPage = () => {
  const { t, i18n } = useTranslation();
  const { countryCode, currency, loading: localizationLoading } = useLocalization();
  const navigate = useNavigate();
  const { toast } = useToast();
  const currentLang = i18n.language || 'es';

  
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [cart, setCart] = useState(null);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [allSavedAddresses, setAllSavedAddresses] = useState([]);
  const [addressesLoaded, setAddressesLoaded] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const shippingRequestSeq = useRef(0);

  // Orders ship only to the storefront's country: saved addresses from other countries are not selectable.
  const sameCountry = (a, b) => (a || '').toUpperCase() === (b || '').toUpperCase();
  const savedAddresses = useMemo(
    () => allSavedAddresses.filter((a) => sameCountry(a.country, countryCode)),
    [allSavedAddresses, countryCode]
  );
  const hiddenAddressCount = allSavedAddresses.length - savedAddresses.length;
  const countryDisplayName = useMemo(() => {
    try { return new Intl.DisplayNames([currentLang], { type: 'region' }).of((countryCode || '').toUpperCase()) || countryCode; }
    catch (e) { return countryCode; }
  }, [currentLang, countryCode]);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  
  // Tax state
  const [taxConfig, setTaxConfig] = useState(null);
  
  // Shipping lock state
  const [shippingLocked, setShippingLocked] = useState(false);
  
  // Email validation state
  const [emailError, setEmailError] = useState('');
  
  // Phone validation state
  const [phoneError, setPhoneError] = useState('');
  
  // Progressive checkout steps
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    phone: '',
    phoneCountryCode: '+57', // Default to Colombia
    street: '',
    number: '',
    apartment: '',
    district: '',
    city: '',
    state: '',
    postalCode: '',
    country: countryCode,
    selectedShippingMethod: null,
    notes: ''
  });

  // The delivery address can only come from a Google Maps suggestion (or a previously saved address):
  // street / number / city / state / postal code are locked and filled by the search box.
  const [addressVerified, setAddressVerified] = useState(false);
  const [addressPickError, setAddressPickError] = useState(false);
  // In countries where Google often lacks the exact house number (see utils/addressRules.js) the shopper
  // types just that number when the picked street came without one.
  const [numberFromUser, setNumberFromUser] = useState(false);
  const numberMissing = numberFromUser && !isValidHouseNumber(formData.number);
  const clearPickedAddress = () => {
    setAddressVerified(false);
    setNumberFromUser(false);
    setFormData(prev => ({ ...prev, street: '', number: '', district: '', city: '', state: '', postalCode: '' }));
  };
  // "Pick a complete address…" with an example in the shopper's own country's style.
  const incompleteAddressMessage = () => {
    const example = addressExample(countryCode);
    return example ? t('checkout.addressIncomplete', { example }) : t('checkout.addressIncompleteGeneric');
  };

  // The form is created before the storefront country is resolved (CO default). Once it is known,
  // default the destination and phone prefix to it — unless the shopper already started filling in an address.
  useEffect(() => {
    if (localizationLoading || !countryCode) return;
    setFormData(prev => (prev.street || prev.city || prev.phone) ? prev : {
      ...prev,
      country: countryCode,
      phoneCountryCode: DIAL_CODE_BY_COUNTRY[countryCode] || '+57',
    });
  }, [countryCode, localizationLoading]);

  useEffect(() => {
    // Wait for the storefront country: the cart's prices/names/sizes come from that country's product version.
    if (localizationLoading) return;
    checkAuthStatus();
    fetchCart();
    fetchShippingOptions(formData.country || countryCode);
    fetchTaxConfig();
  }, [countryCode, localizationLoading]);

  // Re-fetch shipping options whenever the destination country in the form changes
  // (e.g. after Google Places autocomplete or the user picks a saved address).
  useEffect(() => {
    // Wait for the storefront currency so options are never requested (and priced) in the default one.
    if (formData.country && !localizationLoading) {
      fetchShippingOptions(formData.country);
    }
  }, [formData.country, currency, localizationLoading]);

  // Carrier rates (Envía) depend on the full address, which is only known once the
  // address step is done: quote again every time the shopper reaches the shipping step.
  useEffect(() => {
    if (currentStep === 3 && formData.country && !localizationLoading) {
      fetchShippingOptions(formData.country);
    }
  }, [currentStep, formData.postalCode, formData.city, formData.state]);

  useEffect(() => {
    if (isLoggedIn) {
      fetchSavedAddresses();
      // For logged-in users, step 1 (email) is auto-completed
      setCompletedSteps([1]);
      setCurrentStep(2);
    }
  }, [isLoggedIn]);

  // The destination country is always the storefront country.
  useEffect(() => {
    if (countryCode) {
      setFormData(prev => (sameCountry(prev.country, countryCode) ? prev : { ...prev, country: countryCode }));
    }
  }, [countryCode]);

  // Pick the default saved address (of this country) once addresses and country are known.
  useEffect(() => {
    if (!isLoggedIn || localizationLoading || !addressesLoaded) return;
    if (savedAddresses.length > 0) {
      const defaultAddr = savedAddresses.find(a => a.isDefault) || savedAddresses[0];
      setSelectedAddressId(defaultAddr.id);
      fillFormWithAddress(defaultAddr);
      setShowNewAddressForm(false);
    } else {
      setSelectedAddressId(null);
      setShowNewAddressForm(true);
    }
  }, [addressesLoaded, savedAddresses, localizationLoading, isLoggedIn]);

  const checkAuthStatus = async () => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const userData = await response.json();
          setCurrentUser(userData);
          setIsLoggedIn(true);
          
          // Pre-fill email and name
          setFormData(prev => ({
            ...prev,
            email: userData.email,
            fullName: `${userData.firstName} ${userData.lastName}`.trim(),
            phone: userData.phone || ''
          }));
        }
      } catch (error) {
        console.error('Error checking auth:', error);
      }
    }
  };

  const fetchSavedAddresses = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/addresses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const addresses = await response.json();
        setAllSavedAddresses(addresses);
        setAddressesLoaded(true);
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
    }
  };

  const fillFormWithAddress = (address) => {
    // A saved address counts as picked only if it is complete; otherwise the shopper searches again.
    setAddressPickError(false);
    setNumberFromUser(false);
    setAddressVerified(Boolean((address.street || address.addressLine1) && address.city && address.state
      && (address.postalCode || address.zipCode)));
    setFormData(prev => ({
      ...prev,
      fullName: address.fullName || prev.fullName,
      phone: address.phone || prev.phone,
      // Map addressLine1 to street for checkout form
      street: address.street || address.addressLine1 || '',
      number: address.number || '',
      apartment: address.apartment || address.addressLine2 || '',
      district: address.district || '',
      city: address.city || '',
      state: address.state || '',
      // Map zipCode to postalCode for checkout form
      postalCode: address.postalCode || address.zipCode || '',
      country: countryCode
    }));
  };

  const handleAddressSelection = (addressId) => {
    setSelectedAddressId(addressId);
    const selected = savedAddresses.find(a => a.id === addressId);
    if (selected) {
      fillFormWithAddress(selected);
      setShowNewAddressForm(false);
      
      // Validate that the selected address has all required fields
      // Check both field name formats (street/addressLine1 and postalCode/zipCode)
      const hasStreet = selected.street || selected.addressLine1;
      const hasPostalCode = selected.postalCode || selected.zipCode;
      
      if (hasStreet && hasPostalCode && selected.city && selected.state) {
        // Step 2 completed, advance to step 3
        if (!completedSteps.includes(2)) {
          setCompletedSteps([...completedSteps, 2]);
        }
        setCurrentStep(3);
      } else {
        // Show warning that address is incomplete
        const missingFields = [];
        if (!hasStreet) missingFields.push(t('checkout.street'));
        if (!hasPostalCode) missingFields.push(t('checkout.postalCode'));
        if (!selected.city) missingFields.push(t('checkout.city'));
        if (!selected.state) missingFields.push(t('checkoutMore.state'));
        
        toast({
          title: t('checkout.incompleteAddress'),
          description: t('checkout.incompleteAddressMissing', { fields: missingFields.join(', ') }),
          variant: 'destructive'
        });
        // Stay on step 2 so user can fill missing fields
        setShowNewAddressForm(true);
      }
    }
  };

  const validateAndCompleteStep1 = () => {
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!isLoggedIn) {
      if (!formData.email) {
        setEmailError(t('checkoutMore.emailRequired'));
        return false;
      }
      if (!emailRegex.test(formData.email)) {
        setEmailError(t('checkoutMore.emailInvalid'));
        return false;
      }
    }
    
    // Clear error and proceed
    setEmailError('');
    // Keep the e-mail on the cart: an abandoned cart can then be reminded (only with marketing consent).
    if (formData.email) {
      fetch(`${API_URL}/api/cart/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-ID': localStorage.getItem('sessionId') || '' },
        body: JSON.stringify({ email: formData.email, country: formData.country || countryCode }),
      }).catch(() => {});
    }
    if (!completedSteps.includes(1)) {
      setCompletedSteps([...completedSteps, 1]);
    }
    setCurrentStep(2);
    return true;
  };

  const validatePhoneNumber = (phone, countryCode) => {
    // Remove any non-digit characters
    const cleanPhone = phone.replace(/\D/g, '');
    
    // Phone validation rules by dial code (see utils/dialCodes.js)
    const regionNames = (() => {
      try { return new Intl.DisplayNames([currentLang], { type: 'region' }); } catch (e) { return null; }
    })();
    const phoneRules = Object.fromEntries(Object.entries(PHONE_RULES).map(([dial, r]) => [
      dial, { min: r.min, max: r.max, name: regionNames?.of(r.region) || r.region },
    ]));

    const rule = phoneRules[countryCode];
    if (!rule) return { valid: true }; // Unknown country, skip validation
    
    if (cleanPhone.length < rule.min || cleanPhone.length > rule.max) {
      if (rule.min === rule.max) {
        return {
          valid: false,
          message: t('checkoutMore.phoneDigits', { name: rule.name, min: rule.min })
        };
      } else {
        return {
          valid: false,
          message: t('checkoutMore.phoneDigitsRange', { name: rule.name, min: rule.min, max: rule.max })
        };
      }
    }
    
    return { valid: true };
  };

  const validateAndCompleteStep2 = () => {
    // Validate phone number first
    const phoneValidation = validatePhoneNumber(formData.phone, formData.phoneCountryCode);
    if (!phoneValidation.valid) {
      setPhoneError(phoneValidation.message);
      toast({
        title: t('checkout.invalidPhoneNumber'),
        description: phoneValidation.message,
        variant: 'destructive',
      });
      return false;
    }
    
    if (!addressVerified) {
      toast({
        title: t('checkout.incompleteAddress'),
        description: incompleteAddressMessage(),
        variant: 'destructive',
      });
      return false;
    }
    if (numberMissing) {
      toast({
        title: t('checkout.incompleteAddress'),
        description: t('checkout.numberManualHelp'),
        variant: 'destructive',
      });
      return false;
    }

    // Check if ALL required address fields are filled including name and phone
    const missingAddressFields = [];
    if (!formData.fullName) missingAddressFields.push(t('checkout.fullName'));
    if (!formData.phone) missingAddressFields.push(t('checkout.phone'));
    if (!formData.street) missingAddressFields.push(t('checkout.street'));
    if (!formData.city) missingAddressFields.push(t('checkout.city'));
    if (!formData.state) missingAddressFields.push(t('checkoutMore.state'));
    if (!formData.postalCode) missingAddressFields.push(t('checkout.postalCode'));
    if (!formData.country) missingAddressFields.push(t('checkout.country'));
    
    if (missingAddressFields.length > 0) {
      toast({
        title: t('checkout.errors.fillRequired'),
        description: t('checkout.fillFieldsPlease', { fields: missingAddressFields.join(', ') }),
        variant: 'destructive',
      });
      return false;
    }
    
    if (formData.fullName && formData.phone && formData.street && formData.city && 
        formData.state && formData.postalCode && formData.country) {
      setPhoneError(''); // Clear any phone errors
      if (!completedSteps.includes(2)) {
        setCompletedSteps([...completedSteps, 2]);
      }
      setCurrentStep(3);
      return true;
    }
    
    // Show error if trying to continue without all fields
    toast({
      title: t('checkout.incompleteAddress'),
      description: t('checkout.errors.fillRequired'),
      variant: 'destructive',
    });
    return false;
  };

  const validateAndCompleteStep3 = () => {
    // Check if shipping method is selected
    if (formData.selectedShippingMethod) {
      if (!completedSteps.includes(3)) {
        setCompletedSteps([...completedSteps, 3]);
      }
      setCurrentStep(4);
      return true;
    }
    return false;
  };

  const handleEmailBlur = () => {
    validateAndCompleteStep1();
  };

  const handleAddressComplete = () => {
    validateAndCompleteStep2();
  };

  const handleShippingMethodChange = (methodId) => {
    setFormData({ ...formData, selectedShippingMethod: methodId });
    // Don't auto-advance - let user click Continue button
  };

  const handleAddressAutocompleteSelect = (addressData) => {
    // A deliverable address needs a street, its number, a city and a state (e.g. "Calle 122" alone
    // is only a street): reject incomplete picks and make the shopper search again. Where Google often
    // lacks the exact number (Guatemala), a street without one is accepted and the shopper types it.
    const manualNumber = allowsManualNumber(countryCode);
    if (!addressData.street || !addressData.city || !addressData.state || (!addressData.number && !manualNumber)) {
      clearPickedAddress();
      setAddressPickError(true);
      return;
    }
    setAddressPickError(false);
    setAddressVerified(true);
    setNumberFromUser(manualNumber && !addressData.number);
    // Fill the locked fields from Google Maps (name, phone and apartment stay the shopper's).
    // `street` is the street only: the number travels in `number` (labels and e-mails join them).
    setFormData(prev => ({
      ...prev,
      street: addressData.street,
      number: addressData.number || '',
      district: addressData.district || '',
      city: addressData.city,
      state: addressData.state,
      // Some countries have no postal codes; "00000" is what the backend treats as "unknown".
      postalCode: addressData.postalCode || '00000',
      country: countryCode
    }));

    // DO NOT auto-progress to next step
    // User must manually fill name and phone, then click Continue
  };

  const fetchCart = async () => {
    try {
      const sessionId = localStorage.getItem('sessionId') || generateSessionId();
      const response = await fetch(`${API_URL}/api/cart?country=${encodeURIComponent(countryCode || 'CO')}`, {
        headers: { 'X-Session-ID': sessionId }
      });
      const data = await response.json();
      
      setCart(data);
      
      if (data.items.length === 0) {
        toast({
          title: t('checkout.cartEmpty'),
          description: t('checkout.cartEmptyDescription'),
          variant: 'destructive'
        });
        navigate('/shop');
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
      toast({
        title: t('common.error'),
        description: t('checkout.errors.unableToProcess'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchShippingOptions = async (destinationCountry) => {
    const country = destinationCountry || formData.country || countryCode;
    if (!country) return;
    const seq = ++shippingRequestSeq.current;
    setShippingLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/shipping/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          destination: {
            country,
            state: formData.state || '',
            city: formData.city || '',
            postal_code: formData.postalCode || '',
            street: formData.street || ''
          },
          // Storefront-active currency — backend normalizes every option
          // to this so shipping never mixes currencies with product prices.
          currency: currency,
          // Products subtotal: the package is declared/insured for exactly this value.
          goods_value: cart?.subtotal || undefined,
          // Items in the cart: 4-72 EMS is priced by weight.
          item_count: (cart?.items || []).reduce((n, i) => n + (Number(i.quantity) || 1), 0) || undefined,
        })
      });
      const data = await response.json();
      if (seq !== shippingRequestSeq.current) return; // a newer request (other country/currency) supersedes this one
      const options = Array.isArray(data) ? data : (data.options || []);
      setShippingOptions(options);

      // Auto-select the first (cheapest) option. If free shipping is the
      // only option, this locks it in for the user. A choice the shopper already made (e.g. store
      // pickup) is kept when the options are fetched again and it is still on offer.
      if (options.length > 0) {
        setFormData(prev => ({
          ...prev,
          selectedShippingMethod: options.some(o => o.id === prev.selectedShippingMethod) ? prev.selectedShippingMethod : options[0].id,
        }));
      } else {
        setFormData(prev => ({ ...prev, selectedShippingMethod: null }));
      }
    } catch (error) {
      console.error('Error fetching shipping options:', error);
    } finally {
      if (seq === shippingRequestSeq.current) setShippingLoading(false);
    }
  };

  const fetchTaxConfig = async () => {
    if (!countryCode) {
      console.warn('Country code not available yet, skipping tax config fetch');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/localization/countries/${countryCode}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setTaxConfig(data.taxConfig || null);
      console.log('Tax config loaded:', data.taxConfig);
    } catch (error) {
      console.error('Error fetching tax config:', error);
      setTaxConfig(null);
    }
  };

  const generateSessionId = () => {
    const id = Math.random().toString(36).substring(7);
    localStorage.setItem('sessionId', id);
    return id;
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // What the coupon takes off: its amount, plus the selected shipping for a free-shipping coupon (the server does
  // the same sum when the order is placed, so what is shown is what is charged).
  const getCouponDiscount = () => {
    if (!appliedCoupon) return 0;
    const shipping = appliedCoupon.freeShipping ? (getSelectedShipping()?.price || 0) : 0;
    return (appliedCoupon.discountAmount || 0) + shipping;
  };

  const getSelectedShipping = () => {
    return shippingOptions.find(opt => opt.id === formData.selectedShippingMethod);
  };

  const calculateSubtotalWithShipping = () => {
    if (!cart) return 0;
    const selectedShipping = getSelectedShipping();
    const shippingCost = selectedShipping?.price || 0;
    return cart.subtotal + shippingCost;
  };

  const calculateTax = () => {
    if (!cart || !taxConfig || !taxConfig.isEnabled) return 0;
    
    const selectedShipping = getSelectedShipping();
    const shippingCost = selectedShipping?.price || 0;
    let taxableAmount = cart.subtotal + shippingCost;
    
    // Apply coupon discount before calculating tax
    taxableAmount -= getCouponDiscount();
    
    // Calculate tax
    const taxRate = taxConfig.defaultRate / 100;
    const taxAmount = taxableAmount * taxRate;
    
    return Math.max(0, taxAmount);
  };

  const calculateTotal = () => {
    if (!cart) return 0;
    const selectedShipping = getSelectedShipping();
    const shippingCost = selectedShipping?.price || 0;
    let total = cart.subtotal + shippingCost;
    
    // Apply coupon discount if available
    total -= getCouponDiscount();
    
    // Add tax if enabled and not included in price
    if (taxConfig && taxConfig.isEnabled && !taxConfig.includedInPrice) {
      total += calculateTax();
    }
    
    return Math.max(0, total);
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast({
        title: t('common.error'),
        description: t('checkout.couponPlaceholder'),
        variant: 'destructive'
      });
      return;
    }

    try {
      setCouponLoading(true);
      // Guests can use promo codes too: the per-customer limit uses the e-mail typed in step 1.
      const token = localStorage.getItem('accessToken');

      const cartItems = cart.items || [];
      const productIds = cartItems.map(item => item.productId);
      const categoryIds = []; // Extract from products if needed

      const response = await fetch(`${API_URL}/api/coupons/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          code: couponCode.toUpperCase(),
          orderAmount: cart.subtotal,
          productIds,
          categoryIds,
          currency,
          email: formData.email || undefined,
          lang: i18n.language || 'es'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || t('checkoutMore.failedValidateCoupon'));
      }

      if (data.valid) {
        setAppliedCoupon(data);
        toast({
          title: t('checkout.couponApplied'),
          description: data.message
        });
      } else {
        toast({
          title: t('checkout.couponInvalid'),
          description: data.message,
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Apply coupon error:', error);
      toast({
        title: t('common.error'),
        description: error.message || t('checkout.couponInvalid'),
        variant: 'destructive'
      });
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast({
      title: t('checkout.couponRemoved'),
      description: t('checkout.couponRemovedDescription')
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Debug logging
    console.log('Form Data on submit:', {
      email: formData.email,
      fullName: formData.fullName,
      phone: formData.phone,
      street: formData.street,
      city: formData.city,
      state: formData.state,
      postalCode: formData.postalCode,
      selectedShippingMethod: formData.selectedShippingMethod
    });
    
    // Validation - check for missing required fields
    const missingFields = [];
    if (!formData.email) missingFields.push(t('checkout.email'));
    if (!formData.fullName) missingFields.push(t('checkout.fullName'));
    if (!formData.phone) missingFields.push(t('checkout.phone'));
    if (!formData.street) missingFields.push(t('checkout.street'));
    if (!formData.city) missingFields.push(t('checkout.city'));
    if (!formData.state) missingFields.push(t('checkoutMore.state'));
    if (!formData.postalCode) missingFields.push(t('checkout.postalCode'));
    if (!formData.selectedShippingMethod) missingFields.push(t('checkout.shippingMethod'));

    if (!sameCountry(formData.country, countryCode)) {
      toast({
        title: t('checkoutMore.wrongCountryTitle'),
        description: t('checkoutMore.wrongCountry', { country: countryDisplayName }),
        variant: 'destructive'
      });
      return;
    }
    
    if (!addressVerified) {
      toast({
        title: t('checkout.incompleteAddress'),
        description: incompleteAddressMessage(),
        variant: 'destructive'
      });
      return;
    }
    if (numberMissing) {
      toast({
        title: t('checkout.incompleteAddress'),
        description: t('checkout.numberManualHelp'),
        variant: 'destructive'
      });
      return;
    }

    if (missingFields.length > 0) {
      console.error('Missing fields:', missingFields);
      toast({
        title: t('checkout.incompleteInformation'),
        description: t('checkout.fillFieldsPlease', { fields: missingFields.join(', ') }),
        variant: 'destructive'
      });
      return;
    }

    setProcessingPayment(true);

    try {
      // Prepare order request
      const orderRequest = {
        items: cart.items.map(item => ({
          product_id: item.productId,
          product_slug: item.slug || item.productSlug || item.productId,
          product_name: getLocalizedName(item.name, currentLang),
          product_image: item.image?.medium || item.image?.thumbnail || '',
          size: item.size || item.variantId || null,
          quantity: item.quantity,
          price: item.price,
          currency: currency
        })),
        shipping_address: {
          full_name: formData.fullName,
          email: formData.email,
          phone: `${formData.phoneCountryCode}${formData.phone}`,
          street: formData.street,
          number: (formData.number || '').trim(),
          district: formData.district || '',
          apartment: formData.apartment,
          city: formData.city,
          state: formData.state,
          postal_code: formData.postalCode,
          country: formData.country
        },
        storefront_country: countryCode,
        shipping_method_id: formData.selectedShippingMethod,
        notes: formData.notes,
        // Add tax information
        tax_info: taxConfig && taxConfig.isEnabled ? {
          tax_name: taxConfig.taxName,
          tax_rate: taxConfig.defaultRate,
          taxable_amount: calculateSubtotalWithShipping() - getCouponDiscount(),
          tax_amount: calculateTax(),
          included_in_price: taxConfig.includedInPrice,
          country_code: countryCode
        } : null,
        discount_amount: getCouponDiscount(),
        coupon_code: appliedCoupon ? couponCode : null
      };

      // Create payment intent
      const token = localStorage.getItem('accessToken');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Include authorization header if user is logged in
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/api/checkout/create-payment-intent`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(orderRequest)
      });

      if (!response.ok) {
        const error = await response.json();
        const detailMessage = typeof error.detail === 'string' ? error.detail : error.detail?.message;

        // Price changed / item unavailable: show the (localized) server message and refresh the cart
        if (error.detail && typeof error.detail === 'object' && ['PRICE_CHANGED', 'ITEM_UNAVAILABLE'].includes(error.detail.code)) {
          toast({ title: t('checkoutMore.cartUpdatedTitle'), description: detailMessage, variant: 'destructive', duration: 9000 });
          await fetchCart();
          setProcessingPayment(false);
          return;
        }

        // Check for Stripe configuration error (Fix 3)
        if (typeof error.detail === 'string' && (error.detail.includes('Stripe') || error.detail.includes('STRIPE') || error.detail.includes('API key'))) {
          toast({
            title: t('checkout.errors.paymentNotConfigured'),
            description: t('checkout.errors.stripeNotConfigured'),
            variant: 'destructive',
            duration: 8000
          });
          setProcessingPayment(false);
          return;
        }
        
        throw new Error(detailMessage || t('checkoutMore.failedCreatePayment'));
      }

      const paymentData = await response.json();
      
      // Redirect to Stripe checkout
      window.location.href = paymentData.checkout_url;
      
    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        title: t('checkout.checkoutFailedTitle'),
        description: error.message || t('checkout.checkoutFailedDescription'),
        variant: 'destructive'
      });
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#be1f1a] mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const selectedShipping = getSelectedShipping();
  
  const isStepAccessible = (step) => {
    if (step === 1) return true;
    return completedSteps.includes(step - 1);
  };

  const isStepCompleted = (step) => {
    return completedSteps.includes(step);
  };

  const getStepClass = (step) => {
    if (currentStep === step) {
      return 'bg-[#be1f1a] text-white ring-2 ring-[#be1f1a] ring-offset-2';
    }
    if (isStepCompleted(step)) {
      return 'bg-green-500 text-white';
    }
    if (!isStepAccessible(step)) {
      return 'bg-gray-200 text-gray-400';
    }
    return 'bg-gray-200 text-gray-600';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <SEOHead title={t('checkout.title')} url="/checkout" />
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-serif text-[#2C2420] mb-2">{t('checkout.title')}</h1>
          <p className="text-gray-600">{t('checkout.completeYourOrder')}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Information - Step 1 */}
              <div className={`bg-white rounded-lg shadow p-6 ${!isStepAccessible(1) ? 'opacity-50 pointer-events-none' : ''}`}>
                <h2 className="text-xl font-semibold text-[#2C2420] mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-all ${getStepClass(1)}`}>
                      {isStepCompleted(1) ? '✓' : '1'}
                    </span>
                    {t('checkout.contactInformation')}
                  </div>
                  {isStepCompleted(1) && currentStep !== 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setCurrentStep(1);
                        setCompletedSteps(completedSteps.filter(step => step !== 1));
                      }}
                      className="text-[#be1f1a] border-[#be1f1a] hover:bg-[#be1f1a] hover:text-white"
                    >
                      {t('common.edit')}
                    </Button>
                  )}
                </h2>
                
                {currentStep === 1 && (
                  <div className="space-y-4">
                    {!isLoggedIn && (
                      <>
                        <div>
                          <Label htmlFor="email">{t('checkout.email')} *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => {
                              handleInputChange(e);
                              setEmailError(''); // Clear error on change
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                validateAndCompleteStep1();
                              }
                            }}
                            required
                            disabled={currentStep !== 1}
                            className={emailError ? 'border-red-500' : ''}
                          />
                          {emailError && (
                            <p className="text-sm text-red-600 mt-1">{emailError}</p>
                          )}
                        </div>
                        {!isStepCompleted(1) && (
                          <Button
                            type="button"
                            onClick={validateAndCompleteStep1}
                            className="bg-[#be1f1a] hover:bg-[#a71b17]"
                          >
                            {t('checkout.continueToShipping')}
                          </Button>
                        )}
                      </>
                    )}
                    {isLoggedIn && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <p className="text-sm text-green-800">
                          ✓ {t('checkout.loggedInAs')} <strong>{currentUser?.email}</strong>
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Shipping Address - Step 2 */}
              <div className={`bg-white rounded-lg shadow p-6 ${!isStepAccessible(2) ? 'opacity-50 pointer-events-none' : ''}`}>
                <h2 className="text-xl font-semibold text-[#2C2420] mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-all ${getStepClass(2)}`}>
                      {isStepCompleted(2) ? '✓' : '2'}
                    </span>
                    {t('checkout.shippingAddress')}
                  </div>
                  {isStepCompleted(2) && currentStep !== 2 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setCurrentStep(2);
                        setCompletedSteps(completedSteps.filter(step => step !== 2));
                      }}
                      className="text-[#be1f1a] border-[#be1f1a] hover:bg-[#be1f1a] hover:text-white"
                    >
                      {t('common.edit')}
                    </Button>
                  )}
                </h2>
                
                {currentStep === 2 && (
                  <>
                    {isLoggedIn && hiddenAddressCount > 0 && (
                      <p className="text-xs text-gray-500 mb-3" data-testid="hidden-addresses-note">
                        {t('checkoutMore.otherCountryAddressesHidden', { count: hiddenAddressCount, country: countryDisplayName })}
                      </p>
                    )}

                    {/* Saved Addresses Selection (for logged-in users) */}
                    {isLoggedIn && savedAddresses.length > 0 && !showNewAddressForm && (
                      <div className="space-y-3 mb-4">
                    <Label>{t('checkout.selectAddress')}</Label>
                    <RadioGroup value={selectedAddressId} onValueChange={handleAddressSelection}>
                      {savedAddresses.map((address) => (
                        <div key={address.id} className="flex items-start space-x-3 border rounded-lg p-3 hover:border-[#be1f1a] cursor-pointer">
                          <RadioGroupItem value={address.id} id={address.id} className="mt-1" />
                          <label htmlFor={address.id} className="flex-1 cursor-pointer">
                            <div className="font-medium flex items-center gap-2">
                              {address.fullName}
                              {address.isDefault && (
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">{t('checkout.default')}</span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              {address.addressLine1 || address.street} {address.number}{address.apartment && `, ${address.apartment}`}
                            </div>
                            <div className="text-sm text-gray-600">
                              {address.city}, {address.state} {address.zipCode || address.postalCode}, {address.country}
                            </div>
                            {address.phone && (
                              <div className="text-sm text-gray-600">{t('checkout.phone')}: {address.phone}</div>
                            )}
                          </label>
                        </div>
                      ))}
                    </RadioGroup>
                    </div>
                    )}
                    {isLoggedIn && savedAddresses.length > 0 && !showNewAddressForm && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => { clearPickedAddress(); setAddressPickError(false); setShowNewAddressForm(true); }}
                        className="w-full"
                      >
                        {t('checkout.useDifferentAddress')}
                      </Button>
                    )}

                    {/* Address Form (for guests or new address) */}
                    {(!isLoggedIn || showNewAddressForm || savedAddresses.length === 0) && (
                      <>
                    {isLoggedIn && savedAddresses.length > 0 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowNewAddressForm(false);
                          if (savedAddresses.length > 0) {
                            handleAddressSelection(savedAddresses[0].id);
                          }
                        }}
                        className="mb-4"
                      >
                        {t('checkout.backToSavedAddresses')}
                      </Button>
                    )}

                    {/* Google Maps Address Autocomplete */}
                    <div className="mb-4">
                      <Label>{t('checkout.searchAddressLabel')} *</Label>
                      <AddressAutocomplete
                        country={countryCode}
                        onAddressSelect={handleAddressAutocompleteSelect}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {t('checkout.searchAddressHint')}
                      </p>
                      {addressPickError && (
                        <p className="text-sm text-red-600 mt-1" role="alert" data-testid="address-pick-error">
                          {incompleteAddressMessage()}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="fullName">{t('checkout.fullName')} *</Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label htmlFor="phone">{t('checkout.phoneNumber')} *</Label>
                    <div className="flex gap-2">
                      <select
                        name="phoneCountryCode"
                        value={formData.phoneCountryCode}
                        onChange={handleInputChange}
                        className="w-28 h-9 px-3 py-1 border border-input bg-transparent shadow-sm rounded-md focus:outline-none focus:ring-2 focus:ring-[#be1f1a] text-base md:text-sm"
                      >
                        {Object.entries(PHONE_RULES).map(([dial, r]) => (
                          <option key={dial} value={dial}>{flagEmoji(r.region)} {dial}</option>
                        ))}
                      </select>
                      <div className="flex-1">
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => {
                            handleInputChange(e);
                            setPhoneError(''); // Clear error on change
                          }}
                          onBlur={(e) => {
                            // Validate on blur
                            const validation = validatePhoneNumber(e.target.value, formData.phoneCountryCode);
                            if (!validation.valid && e.target.value) {
                              setPhoneError(validation.message);
                            }
                          }}
                          placeholder={t('checkoutMore.phonePlaceholder')}
                          className={phoneError ? 'border-red-500' : ''}
                          required
                        />
                        {phoneError && (
                          <p className="text-sm text-red-600 mt-1">{phoneError}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="street">{t('checkout.street')} *</Label>
                    <Input
                      id="street"
                      name="street"
                      value={formData.street}
                      placeholder={t('checkout.addressLockedPlaceholder')}
                      disabled
                      className={LOCKED_FIELD}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="number">{t('checkout.number')}{numberFromUser ? ' *' : ''}</Label>
                    <Input
                      id="number"
                      name="number"
                      data-testid="house-number-input"
                      value={formData.number}
                      placeholder={numberFromUser ? t('checkout.numberManualPlaceholder') : t('checkout.addressLockedPlaceholder')}
                      disabled={!numberFromUser}
                      maxLength={20}
                      className={numberFromUser ? (numberMissing ? 'border-red-400' : '') : LOCKED_FIELD}
                      onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                    />
                    {numberFromUser && (
                      <p className="text-xs text-gray-500 mt-1" data-testid="house-number-help">{t('checkout.numberManualHelp')}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="apartment">{t('checkout.apartment')}</Label>
                    <Input
                      id="apartment"
                      name="apartment"
                      value={formData.apartment}
                      onChange={handleInputChange}
                    />
                  </div>

                  {formData.district && (
                    <div className="md:col-span-2">
                      <Label htmlFor="district">{t('checkout.district')}</Label>
                      <Input id="district" name="district" data-testid="district-input" value={formData.district} disabled className={LOCKED_FIELD} />
                    </div>
                  )}

                  <div>
                    <Label htmlFor="city">{t('checkout.city')} *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      placeholder={t('checkout.addressLockedPlaceholder')}
                      disabled
                      className={LOCKED_FIELD}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="state">{t('checkout.state')} *</Label>
                    <Input
                      id="state"
                      name="state"
                      value={formData.state}
                      placeholder={t('checkout.addressLockedPlaceholder')}
                      disabled
                      className={LOCKED_FIELD}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="postalCode">{t('checkout.postalCode')} *</Label>
                    <Input
                      id="postalCode"
                      name="postalCode"
                      value={formData.postalCode}
                      placeholder={t('checkout.addressLockedPlaceholder')}
                      disabled
                      className={LOCKED_FIELD}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="country">{t('checkout.country')}</Label>
                    <LockedCountrySelect id="country" />
                    <p className="text-xs text-gray-500 mt-1">
                      {t('checkoutMore.shipOnlyTo', { country: countryDisplayName })}
                    </p>
                  </div>
                </div>

                    {/* Continue Button for Step 2 */}
                    {(!isLoggedIn || showNewAddressForm || savedAddresses.length === 0) && (
                      <Button
                        type="button"
                        onClick={handleAddressComplete}
                        className="w-full bg-[#be1f1a] hover:bg-[#a71b17] mt-4"
                        disabled={!addressVerified || numberMissing || !formData.fullName || !formData.phone || !formData.street || !formData.city || !formData.state || !formData.postalCode || !formData.country}
                      >
                        {t('checkout.continueToShippingMethod')}
                      </Button>
                    )}
                  </>
                )}
                  </>
                )}
              </div>

              {/* Shipping Method - Step 3 */}
              <div className={`bg-white rounded-lg shadow p-6 ${!isStepAccessible(3) ? 'opacity-50 pointer-events-none' : ''}`}>
                <h2 className="text-xl font-semibold text-[#2C2420] mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-all ${getStepClass(3)}`}>
                      {isStepCompleted(3) ? '✓' : '3'}
                    </span>
                    {t('checkout.shippingMethod')}
                  </div>
                  {isStepCompleted(3) && currentStep !== 3 && shippingLocked && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setCurrentStep(3);
                        setShippingLocked(false);
                        setCompletedSteps(completedSteps.filter(step => step !== 3));
                      }}
                      className="text-[#be1f1a] border-[#be1f1a] hover:bg-[#be1f1a] hover:text-white"
                    >
                      {t('common.edit')}
                    </Button>
                  )}
                </h2>
                
                {currentStep === 3 && !shippingLocked && (
                  <>
                    {shippingLoading && (
                      <p className="text-sm text-gray-600 mb-3">{t('checkout.loadingShipping')}</p>
                    )}
                    {!shippingLoading && shippingOptions.length === 0 && (
                      <p className="text-sm text-[#be1f1a] mb-3">{t('checkout.noShippingOptions')}</p>
                    )}
                    <RadioGroup
                      value={formData.selectedShippingMethod}
                      onValueChange={handleShippingMethodChange}
                      className="space-y-3"
                    >
                      {shippingOptions.map((option) => (
                        <div key={option.id} className="flex items-center space-x-3 border rounded-lg p-4 hover:border-[#be1f1a] cursor-pointer">
                          <RadioGroupItem value={option.id} id={option.id} />
                          <label htmlFor={option.id} className="flex-1 cursor-pointer">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium flex items-center gap-2">
                                  {option.provider === 'pickup'
                                    ? <Store className="w-4 h-4 text-[#be1f1a]" />
                                    : <Truck className="w-4 h-4 text-[#be1f1a]" />}
                                  {option.name}
                                </div>
                                <div className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                                  <Clock className="w-3 h-3" />
                                  {option.description}
                                </div>
                              </div>
                              <div className="text-lg font-semibold text-[#be1f1a]">
                                {formatPrice(option.price, option.currency)}
                              </div>
                            </div>
                          </label>
                        </div>
                      ))}
                    </RadioGroup>

                    {/* Continue Button for Step 3 */}
                    {formData.selectedShippingMethod && !shippingLoading && (
                      <Button
                        type="button"
                        onClick={() => {
                          if (!completedSteps.includes(3)) {
                            setCompletedSteps([...completedSteps, 3]);
                          }
                          setShippingLocked(true);
                          setCurrentStep(4);
                        }}
                        className="w-full bg-[#be1f1a] hover:bg-[#a71b17] mt-4"
                      >
                        {t('checkout.continueToPayment')}
                      </Button>
                    )}
                  </>
                )}
                
                {/* Show selected shipping when locked */}
                {shippingLocked && selectedShipping && (
                  <div className="border-2 border-green-500 rounded-lg p-4 bg-green-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          <Truck className="w-4 h-4 text-green-600" />
                          {selectedShipping.name}
                        </div>
                        <div className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3" />
                          {selectedShipping.description}
                        </div>
                      </div>
                      <div className="text-lg font-semibold text-green-600">
                        {formatPrice(selectedShipping.price, selectedShipping.currency)}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Order Notes - Optional */}
              {currentStep >= 3 && (
                <div className={`bg-white rounded-lg shadow p-6 ${!isStepAccessible(3) ? 'opacity-50 pointer-events-none' : ''}`}>
                  <h2 className="text-xl font-semibold text-[#2C2420] mb-4">{t('checkout.orderNotes')}</h2>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#be1f1a]"
                    placeholder={t('checkout.orderNotesPlaceholder')}
                    disabled={currentStep < 3}
                  />
                </div>
              )}

              {/* Payment - Step 4 */}
              <div className={`bg-white rounded-lg shadow p-6 ${!isStepAccessible(4) ? 'opacity-50 pointer-events-none' : ''}`}>
                <h2 className="text-xl font-semibold text-[#2C2420] mb-4 flex items-center gap-2">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-all ${getStepClass(4)}`}>
                    {isStepCompleted(4) ? '✓' : '4'}
                  </span>
                  {t('checkout.payment')}
                </h2>
                
                {currentStep === 4 && (
                  <>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 text-blue-900">
                    <Lock className="w-4 h-4" />
                    <span className="font-medium">{t('checkout.securePayment')}</span>
                  </div>
                  <p className="text-sm text-blue-800 mt-1">
                    {t('checkout.stripeRedirectMessage')}
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={processingPayment || !formData.selectedShippingMethod}
                  className="w-full bg-[#be1f1a] hover:bg-[#a71b17] h-12 text-lg"
                >
                  {processingPayment ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      {t('checkout.processingOrder')}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5" />
                      {t('checkout.placeOrder')}
                    </span>
                  )}
                </Button>
                  </>
                )}
              </div>
            </form>
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6 sticky top-8">
              <h3 className="text-lg font-semibold text-[#2C2420] mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-[#be1f1a]" />
                {t('checkout.orderSummary')}
              </h3>
              
              {cart && (
                <>
                  <div className="space-y-4 mb-4">
                    {cart.items.map((item, index) => (
                      <div key={index} className="flex gap-3">
                        <img
                          src={item.image?.thumbnail || 'https://placehold.co/60x80'}
                          alt={getLocalizedName(item.name, currentLang)}
                          className="w-16 h-20 object-cover rounded"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium line-clamp-2">{getLocalizedName(item.name, currentLang)}</p>
                          {item.size && item.size !== 'Única' && (
                            <p className="text-sm text-gray-500">{t('checkout.size')}: {item.size}</p>
                          )}
                          <p className="text-sm text-gray-500">{t('checkout.qty')}: {item.quantity}</p>
                          <p className="text-sm font-semibold text-[#be1f1a]">
                            {formatPrice(item.price * item.quantity, currency)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Separator className="my-4" />

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{t('checkout.subtotal')}:</span>
                      <span className="font-medium">{formatPrice(cart.subtotal, currency)}</span>
                    </div>
                    
                    {selectedShipping && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">{t('checkout.shipping')}:</span>
                        <span className="font-medium">{formatPrice(selectedShipping.price, selectedShipping.currency)}</span>
                      </div>
                    )}

                    {appliedCoupon && getCouponDiscount() > 0 && (
                      <div className="flex justify-between text-sm text-green-600">
                        <span className="flex items-center gap-1">
                          {t('checkout.discount')} ({couponCode}):
                          <button
                            onClick={handleRemoveCoupon}
                            className="text-red-500 hover:text-red-700 ml-1"
                            title={t('checkout.couponRemoveTitle')}
                          >
                            ✕
                          </button>
                        </span>
                        <span className="font-medium">-{formatPrice(getCouponDiscount(), currency)}</span>
                      </div>
                    )}

                    {/* Tax Display */}
                    {taxConfig && taxConfig.isEnabled && taxConfig.displayOnCheckout && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">
                          {taxConfig.taxName} ({taxConfig.defaultRate}%):
                          {taxConfig.includedInPrice && (
                            <span className="text-xs text-gray-500 ml-1">({t('checkout.taxIncluded').toLowerCase()})</span>
                          )}
                        </span>
                        <span className="font-medium">
                          {taxConfig.includedInPrice ? (
                            <span className="text-gray-500">{t('checkout.taxIncluded')}</span>
                          ) : (
                            formatPrice(calculateTax(), currency)
                          )}
                        </span>
                      </div>
                    )}
                  </div>

                  <Separator className="my-4" />

                  {/* Coupon Input */}
                  {!appliedCoupon && (
                    <div className="mb-4">
                      <Label htmlFor="coupon" className="text-sm font-medium mb-2">{t('checkout.haveCouponCode')}</Label>
                      <div className="flex gap-2">
                        <Input
                          id="coupon"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          placeholder={t('checkout.couponPlaceholder')}
                          className="flex-1"
                          onKeyPress={(e) => e.key === 'Enter' && handleApplyCoupon()}
                        />
                        <Button
                          onClick={handleApplyCoupon}
                          disabled={couponLoading || !couponCode.trim()}
                          className="bg-[#2C2420] hover:bg-[#3d332c]"
                        >
                          {couponLoading ? t('checkout.couponApplying') : t('checkout.couponApply')}
                        </Button>
                      </div>
                    </div>
                  )}

                  <Separator className="my-4" />

                  <div className="flex justify-between text-lg font-semibold">
                    <span className="text-[#2C2420]">{t('orders.total')}:</span>
                    <span className="text-[#be1f1a]">
                      {formatPrice(calculateTotal(), currency)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewCheckoutPage;
